import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Globe, Mail, MapPin, Phone, Facebook, Twitter, Linkedin, Instagram, Server, Users, Headphones, Shield, Menu, X, ShoppingCart, Truck, CreditCard, BarChart2, Cloud, Package, CheckCircle, Check, RefreshCw, Zap, Layers } from 'lucide-react';
import logo from './assets/logo1.png';
import homeBg from './assets/home.jpg';
import alliedUniversalLogo from './assets/clients/allied-universal.png';
import centerlineLogo from './assets/clients/centerline-coffee.jpg';
import dsdAssistLogo from './assets/clients/dsd-assist.png';
import wearertechLogo from './assets/clients/wearertech.png';
import mechanicSuperstoreLogo from './assets/clients/mechanic-superstore.png';
import totalPhLogo from './assets/clients/totalph.png';
import jigxLogo from './assets/clients/jigx.svg';
import zmgLogo from './assets/clients/zmg-fasteners.svg';
import dnbLogo from './assets/clients/dnb.svg';
import acetLogo from './assets/clients/acet.png';
import habikonLogo from './assets/clients/habikon.svg';
import somoLogo from './assets/clients/somo-africa.png';
import cart2leadImg from './assets/products/cart2lead.png';
import leadbridgeImg from './assets/products/leadbridge.png';
import goldsyncImg from './assets/products/goldsync.png';
import royalshipImg from './assets/products/royalship.png';
import poflowImg from './assets/products/poflow.png';
import billflowImg from './assets/products/billflow.png';
import intelxAiImg from './assets/products/intelx-ai.png';
import stockpulseImg from './assets/products/stockpulse.png';

// ...existing imports
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';







const IndustrySolutions = () => {
  const industries = [
    { name: "Manufacturing", description: "Optimize production, manage inventory, and improve supply chain efficiency." },
    { name: "Retail", description: "Streamline sales, inventory, and customer management for both online and physical stores." },
    { name: "Distribution", description: "Efficiently manage product distribution, warehousing, and order fulfillment." },
    { name: "Construction", description: "Plan projects, manage resources, and track costs effectively in construction." },
    { name: "Agriculture", description: "Manage farm operations, supply chains, and agricultural production efficiently." },
    { name: "Software & Technology", description: "Enhance software development, IT operations, and technology-driven workflows with ERP." }
  ];

  return (
    <section className="py-16 bg-gray-100">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Industry Solutions</h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          We provide ERP solutions tailored to major industries, helping businesses operate efficiently and intelligently.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry, index) => (
            <div 
              key={index} 
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition"
            >
              <h3 className="text-xl font-semibold text-[#004aad] mb-2">{industry.name}</h3>
              <p className="text-gray-600">{industry.description}</p>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-600 mt-8 font-medium">…and many more industries</p>
      </div>
    </section>
  );
};




// Homepage showcase of IntelXcode products for Acumatica
const AcumaticaIntegrations = ({ onBrowse }) => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-6">
      <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">
        Our Acumatica Products
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
        Ready-to-use integrations, automation and AI products built by IntelXcode —
        helping Acumatica businesses and consultants reduce manual work and get more from their ERP.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
        {PRODUCTS.map((product) => (
          <div
            key={product.id}
            onClick={() => onBrowse(product.id)}
            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
          >
            <img src={product.image} alt={product.name} className="w-full object-cover" />
            <div className="p-4 text-center">
              <h3 className="font-bold text-gray-800">{product.name}</h3>
              <p className="text-xs text-gray-500 mt-1">{product.tagline}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={() => onBrowse(null)}
          className="bg-[#004aad] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#003a8d] transition-colors duration-300 inline-flex items-center"
        >
          Explore Products and Pricing <ChevronRight className="ml-2" size={20} />
        </button>
        <p className="text-gray-600 mt-6">
          Every subscription includes setup, configuration, monitoring and support.
        </p>
      </div>
    </div>
  </section>
);

const AIBusinessBoostSection = () => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-6 flex flex-col md:flex-row items-center gap-12">
      {/* Text */}
      <div className="md:w-1/2 text-center md:text-left">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">Boost Your Business with AI</h2>
        <p className="text-lg text-gray-600 mb-6">
          Harness the power of AI-enhanced ERP solutions to automate workflows, 
          gain predictive insights, and optimize operations. Make smarter, 
          data-driven decisions and scale your business efficiently.
        </p>
        <ul className="space-y-4 text-gray-700">
          <li className="flex items-start">
            <span className="text-[#004aad] mr-2 mt-1">&#9656;</span>
            Streamline repetitive tasks with intelligent automation
          </li>
          <li className="flex items-start">
            <span className="text-[#004aad] mr-2 mt-1">&#9656;</span>
            Forecast trends and optimize resources with predictive insights
          </li>
          <li className="flex items-start">
            <span className="text-[#004aad] mr-2 mt-1">&#9656;</span>
            Increase efficiency across departments and processes
          </li>
        </ul>
      </div>

      {/* Image */}
      <div className="md:w-1/2">
        <img 
          src="https://itchronicles.com/wp-content/uploads/2024/06/fusion-of-ERP-and-AI-1024x683.jpg" 
          alt="ERP AI Power" 
          className="rounded-xl shadow-lg w-full object-cover"
        />
      </div>
    </div>
  </section>
);



// FAQ Section Component
const FAQSection = () => {
  const faqs = [
    {
      question: "Can you customize ERP solutions for specific industries?",
      answer: "Yes, we tailor ERP implementations to meet industry-specific workflows and compliance requirements."
    },
    {
      question: "Do you offer cloud migration services?",
      answer: "Absolutely, we provide secure migration from on-premise systems to cloud-based ERP platforms with minimal disruption."
    },
    {
      question: "Do you provide training for ERP users?",
      answer: "Yes, we offer comprehensive training programs to ensure your team can fully utilize the ERP system."
    },
    {
      question: "Can AI be integrated with my existing ERP?",
      answer: "Yes, we offer AI-powered enhancements that automate workflows and provide predictive insights within your ERP."
    },
    {
      question: "Do you offer post-implementation support?",
      answer: "Yes, our team provides ongoing support and consulting to ensure smooth operations and continuous optimization."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition">
              <h3 className="text-xl font-semibold text-[#004aad] mb-2">{faq.question}</h3>
              <p className="text-gray-600">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Counter Section Component
const CounterSection = () => {
  const stats = [
    { id: 1, label: 'Clients', value: 20 },
    { id: 2, label: 'Projects Completed', value: 50 },
    { id: 3, label: 'Awards Won', value: 2 },
    { id: 4, label: 'Countries Served', value: 9 },
  ];

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const [counts, setCounts] = React.useState(stats.map(() => 0));

  useEffect(() => {
    if (inView) {
      stats.forEach((stat, index) => {
        let start = 0;
        const increment = Math.ceil(stat.value / 100);
        const interval = setInterval(() => {
          start += increment;
          if (start >= stat.value) {
            start = stat.value;
            clearInterval(interval);
          }
          setCounts((prev) => {
            const newCounts = [...prev];
            newCounts[index] = start;
            return newCounts;
          });
        }, 20);
      });
    }
  }, [inView]);

  return (
    <section ref={ref} className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((stat, index) => (
            <div key={stat.id} className="bg-gray-50 p-6 rounded-lg shadow hover:shadow-lg transition">
              <h3 className="text-4xl font-bold text-[#004aad]">{counts[index]}+</h3>
              <p className="text-gray-600 mt-2">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};



// Trusted By Section Component
const TrustedBySection = () => {
  const clients = [
    { name: 'Allied Universal', logo: alliedUniversalLogo },
    { name: 'Centerline Coffee Roasting', logo: centerlineLogo },
    { name: 'DSD Assist', logo: dsdAssistLogo },
    { name: 'WearerTech', logo: wearertechLogo },
    { name: 'Mechanic Superstore', logo: mechanicSuperstoreLogo },
    { name: 'Total Plumbing & Heating', logo: totalPhLogo },
    { name: 'Jigx', logo: jigxLogo },
    { name: 'ZMG Fasteners Plus', logo: zmgLogo },
    { name: 'Dun & Bradstreet', logo: dnbLogo },
    { name: 'AC-ET', logo: acetLogo },
    { name: 'Habikon', logo: habikonLogo },
    { name: 'Somo Africa', logo: somoLogo },
  ];

  const scrollSpeed = 30; // seconds per full loop — lower = faster

  return (
    <section className="py-16 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">Trusted by Leading Companies</h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Organizations across industries and continents rely on IntelxCode for their ERP, CRM, and payroll transformation.
        </p>
      </div>

      {/* Scrolling logo marquee — pauses on hover */}
      <div className="relative w-full overflow-hidden">
        <div
          className="flex w-max gap-6 pr-6 animate-marquee hover:[animation-play-state:paused]"
          style={{ animationDuration: `${scrollSpeed}s` }}
        >
          {[...clients, ...clients].map((client, index) => (
            <div
              key={`${client.name}-${index}`}
              title={client.name}
              className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 flex items-center justify-center p-6 h-28 w-52 flex-shrink-0"
            >
              <img
                src={client.logo}
                alt={client.name}
                className="max-h-16 max-w-full object-contain"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// ===================== PRODUCTS MARKETPLACE =====================
// IntelXcode ready-to-use integrations, automation and AI products for Acumatica.
// To add a product, copy an object below and give it a unique id.

const PRODUCT_GROUPS = [
  { id: 'integrations', label: 'Integrations', color: '#004aad',
    blurb: 'Ready-to-use connectors that keep Acumatica talking to the platforms you sell and ship on.' },
  { id: 'automation', label: 'Automation', color: '#6d28d9',
    blurb: 'AI-powered document automation that kills manual data entry in Acumatica.' },
  { id: 'ai', label: 'AI & Intelligence', color: '#047857',
    blurb: 'Intelligence layers that turn your Acumatica data into answers, alerts and action.' },
];

const PRODUCTS = [
  {
    id: 'cart2lead',
    group: 'integrations',
    name: 'Cart2Lead',
    image: cart2leadImg,
    tagline: 'Shopify Abandoned Cart → Acumatica Leads',
    positioning: 'Turn abandoned Shopify carts into actionable Acumatica leads.',
    problem: 'Businesses lose potential sales when customers abandon their Shopify carts and the sales team has no structured way to follow up.',
    solution: 'Cart2Lead automatically collects abandoned carts from Shopify and creates corresponding leads in Acumatica.',
    benefits: [
      'Recover potential lost sales',
      'Automatically create Acumatica leads',
      'Eliminate manual data entry',
      'Give sales teams visibility of abandoned-cart opportunities',
      'Connect Shopify with Acumatica',
    ],
    plans: [
      { name: 'Starter', price: '$49', period: '/month', features: ['1 Shopify store', 'Up to 500 abandoned carts / month', 'Automatic lead creation in Acumatica', 'Email support'], highlighted: false },
      { name: 'Growth', price: '$99', period: '/month', features: ['3 stores', 'Unlimited abandoned carts', 'Near real-time sync', 'Priority support'], highlighted: true },
      { name: 'Scale', price: 'Custom', period: '', features: ['Unlimited stores', 'Custom lead-mapping rules', 'Dedicated account manager'], highlighted: false },
    ],
  },
  {
    id: 'leadbridge',
    group: 'integrations',
    name: 'LeadBridge',
    image: leadbridgeImg,
    tagline: 'Facebook Leads → Acumatica',
    positioning: 'Turn Facebook leads into Acumatica opportunities automatically.',
    problem: 'Businesses generate leads through Facebook but often have to manually transfer those leads into their ERP or CRM.',
    solution: 'LeadBridge automatically sends Facebook leads directly into Acumatica.',
    benefits: [
      'Automatic lead creation',
      'Faster sales follow-up',
      'Eliminate manual data entry',
      'Centralize leads in Acumatica',
      'Connect marketing campaigns with the ERP',
    ],
    plans: [
      { name: 'Starter', price: '$49', period: '/month', features: ['1 Facebook page', 'Up to 500 leads / month', 'Automatic lead creation', 'Email support'], highlighted: false },
      { name: 'Growth', price: '$99', period: '/month', features: ['3 pages / ad accounts', 'Unlimited leads', 'Instant sync', 'Priority support'], highlighted: true },
      { name: 'Scale', price: 'Custom', period: '', features: ['Unlimited pages', 'Custom field mapping', 'Dedicated account manager'], highlighted: false },
    ],
  },
  {
    id: 'goldsync',
    group: 'integrations',
    name: 'GoldSync',
    image: goldsyncImg,
    tagline: 'GoldVision ↔ Acumatica',
    positioning: 'Keep GoldVision and Acumatica working from the same customer data.',
    problem: 'Businesses using both GoldVision and Acumatica may have customer and business-account information spread across multiple systems.',
    solution: 'GoldSync connects GoldVision with Acumatica and synchronizes relevant business account information.',
    benefits: [
      'Reduce duplicate data entry',
      'Keep customer information synchronized',
      'Reduce data inconsistencies',
      'Improve visibility across business applications',
    ],
    plans: [
      { name: 'Standard', price: '$79', period: '/month', features: ['Two-way account sync', 'Scheduled or near real-time', 'Duplicate detection', 'Priority support'], highlighted: true },
      { name: 'Custom', price: 'Custom', period: '', features: ['Custom entities & fields', 'Historical data migration', 'Dedicated account manager'], highlighted: false },
    ],
  },
  {
    id: 'royalship',
    group: 'integrations',
    name: 'RoyalShip',
    image: royalshipImg,
    tagline: 'Royal Mail (Click & Drop) ↔ Acumatica',
    positioning: 'Connect Acumatica directly to Royal Mail shipping workflows.',
    problem: 'Businesses using Royal Mail may need to manually move shipping information between their ERP and shipping operations.',
    solution: 'RoyalShip connects Royal Mail (Click & Drop) with Acumatica to streamline shipping and fulfillment workflows.',
    benefits: [
      'Reduce manual shipping work',
      'Connect ERP and shipping operations',
      'Improve fulfillment efficiency',
      'Reduce data-entry errors',
    ],
    plans: [
      { name: 'Standard', price: '$79', period: '/month', features: ['1 Royal Mail account', 'Automatic label & tracking sync', 'Priority support'], highlighted: true },
      { name: 'Scale', price: 'Custom', period: '', features: ['Multiple accounts / sites', 'Custom shipping rules', 'Dedicated account manager'], highlighted: false },
    ],
  },
  {
    id: 'poflow',
    group: 'automation',
    name: 'POFlow AI',
    image: poflowImg,
    tagline: 'Purchase Order Automation for Acumatica',
    positioning: 'Turn purchase order PDFs into Acumatica transactions automatically.',
    problem: 'Businesses receive purchase orders as PDFs and spend significant time manually reading, entering, and validating the information in Acumatica.',
    solution: 'POFlow AI automates the process of converting purchase order documents into Acumatica transactions.',
    workflow: ['PDF Purchase Order', 'AI / Data Extraction', 'Field Mapping', 'Acumatica', 'Document Attachment'],
    benefits: [
      'Reduce manual PO entry',
      'Process documents faster',
      'Reduce data-entry mistakes',
      'Standardize order processing',
      'Preserve the original document',
    ],
    plans: [
      { name: 'Starter', price: '$99', period: '/month', features: ['Up to 200 PO documents / month', 'AI extraction & field mapping', 'Original document attached', 'Email support'], highlighted: false },
      { name: 'Growth', price: '$249', period: '/month', features: ['Up to 1,000 documents / month', 'Validation rules', 'Priority support'], highlighted: true },
      { name: 'Enterprise', price: 'Custom', period: '', features: ['Unlimited volume', 'Custom templates', 'Dedicated account manager'], highlighted: false },
    ],
  },
  {
    id: 'billflow',
    group: 'automation',
    name: 'BillFlow AI',
    image: billflowImg,
    tagline: 'AI-Powered Supplier Bills → Acumatica',
    positioning: 'Let AI turn supplier invoices into Acumatica bills.',
    problem: 'Accounts teams manually enter supplier invoices and bills into Acumatica, consuming valuable time and increasing the possibility of data-entry errors.',
    solution: 'BillFlow AI uses AI to read supplier bills, extract important information, map the data to Acumatica, and automate bill creation.',
    workflow: ['Supplier Bill', 'AI Extraction', 'Data Mapping', 'Validation', 'Acumatica Bill'],
    benefits: [
      'Reduce manual AP data entry',
      'Process supplier bills faster',
      'Reduce transcription errors',
      'Standardize invoice processing',
      'Save accounting staff time',
    ],
    plans: [
      { name: 'Starter', price: '$99', period: '/month', features: ['Up to 200 bills / month', 'AI extraction & mapping', 'Validation before posting', 'Email support'], highlighted: false },
      { name: 'Growth', price: '$249', period: '/month', features: ['Up to 1,000 bills / month', 'Multi-entity support', 'Priority support'], highlighted: true },
      { name: 'Enterprise', price: 'Custom', period: '', features: ['Unlimited volume', 'Custom approval flows', 'Dedicated account manager'], highlighted: false },
    ],
  },
  {
    id: 'intelx-ai',
    group: 'ai',
    name: 'IntelX AI',
    image: intelxAiImg,
    tagline: 'AI Assistant for Acumatica Consultants',
    positioning: 'Your AI-powered Acumatica development and consulting assistant.',
    problem: 'Acumatica users often spend significant time understanding their system, creating documents, troubleshooting errors, and finding the right way to configure, customize, or extend their ERP.',
    solution: 'IntelX AI acts as your Acumatica AI Consultant, helping you understand, solve, and build within Acumatica - from everyday documents and system questions to troubleshooting, customizations, integrations, and development.',
    capabilities: [
      'Document Creation',
      'System Understanding',
      'Error analysis & troubleshooting',
      'Acumatica development assistance',
      'Customization guidance',
      'API assistance',  
      'DAC and Graph assistance',
      'Report development guidance',
      'Integration assistance',
      'Code generation',
      'Technical Problem Solving'
    ],
    benefits: [
      'Research solutions in seconds, not hours',
      'Debug errors with AI-guided analysis',
      'Generate customization code faster',
      'Get expert Acumatica guidance on demand',
    ],
    plans: [
      { name: 'Consultant', price: '$49', period: '/user/month', features: ['Full AI assistant access', 'Development & API help', 'BQL / DAC / Graph assistance', 'Email support'], highlighted: false },
      { name: 'Team', price: '$99', period: '/user/month', features: ['Everything in Consultant', '5+ users', 'Shared knowledge base', 'Priority support'], highlighted: true },
      { name: 'Enterprise', price: 'Custom', period: '', features: ['Private deployment', 'Your own documentation trained in', 'Dedicated success manager'], highlighted: false },
    ],
  },
  {
    id: 'stockpulse',
    group: 'ai',
    name: 'StockPulse',
    image: stockpulseImg,
    tagline: 'Inventory Intelligence & Automated Alerts for Acumatica',
    positioning: 'Stop checking inventory. Let StockPulse tell you what needs attention.',
    problem: 'Businesses often have important inventory changes buried in Acumatica and must manually check reports to identify products that need attention.',
    solution: 'StockPulse continuously monitors Acumatica inventory and provides actionable insights and automated alerts when important events occur.',
    intelligence: [
      'Newly Listed — products newly available in selected branches',
      'Replenished — products previously out of stock that are now available',
      'Out of Stock — products that are currently unavailable',
      'Slow Moving — products with no or low sales over a customizable period',
      'Low Stock — products below a user-defined stock level',
      'Custom Rules — create your own inventory monitoring conditions',
    ],
    notifications: [
      'Automated email notifications',
      'Dashboard alerts',
      'Scheduled daily/weekly inventory summaries',
      'Branch-specific alerts',
    ],
    benefits: [
      'Proactively monitor inventory',
      'Reduce manual report checking',
      'Identify stock issues faster',
      'Customize inventory rules',
      'Improve inventory decisions',
      'Turn Acumatica data into actionable intelligence',
    ],
    plans: [
      { name: 'Starter', price: '$99', period: '/month', features: ['1 branch', 'Core alerts (low / out of stock)', 'Daily email summary', 'Email support'], highlighted: false },
      { name: 'Growth', price: '$149', period: '/month', features: ['Up to 5 branches', 'All intelligence rules', 'Custom rules & thresholds', 'Priority support'], highlighted: true },
      { name: 'Enterprise', price: 'Custom', period: '', features: ['Unlimited branches', 'API access to alerts', 'Dedicated account manager'], highlighted: false },
    ],
  },
];

// ---------- Shared bits ----------
const groupById = (id) => PRODUCT_GROUPS.find((g) => g.id === id);

const PricingPlans = ({ plans, onContact }) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 ${plans.length >= 3 ? 'lg:grid-cols-3' : ''} gap-8 mb-12`}>
    {plans.map((plan) => (
      <div
        key={plan.name}
        className={`rounded-xl p-8 flex flex-col ${
          plan.highlighted
            ? 'bg-white shadow-2xl border-2 border-[#004aad] relative'
            : 'bg-white shadow-lg border border-gray-100'
        }`}
      >
        {plan.highlighted && (
          <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#004aad] text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
            Most Popular
          </span>
        )}
        <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
        <div className="mb-6">
          <span className="text-4xl font-bold text-[#004aad]">{plan.price}</span>
          <span className="text-gray-500">{plan.period}</span>
        </div>
        <ul className="space-y-3 mb-8 flex-grow">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start text-gray-700">
              <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={onContact}
          className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-300 ${
            plan.highlighted
              ? 'bg-[#004aad] text-white hover:bg-[#003a8d]'
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          }`}
        >
          Get Started
        </button>
      </div>
    ))}
  </div>
);

// ---------- Marketplace page ----------
const IntegrationsMarketplace = ({ onSelect, onContact }) => {
  const [group, setGroup] = useState('all');
  const groups = [{ id: 'all', label: 'All Products' }, ...PRODUCT_GROUPS];
  const visible = group === 'all' ? PRODUCTS : PRODUCTS.filter((p) => p.group === group);

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Integrations, Automation & AI Products for Acumatica</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            IntelXcode builds ready-to-use integrations, automation solutions, and AI-powered products that help
            Acumatica businesses and consultants reduce manual work, improve efficiency, and get more value from their ERP.
          </p>
        </div>

        {/* Group filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-14">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => setGroup(g.id)}
              className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-colors duration-300 ${
                group === g.id ? 'bg-[#004aad] text-white shadow-md' : 'bg-white text-gray-700 border border-gray-200 hover:border-[#004aad] hover:text-[#004aad]'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        {/* Grouped product cards */}
        {(group === 'all' ? PRODUCT_GROUPS : PRODUCT_GROUPS.filter((g) => g.id === group)).map((g) => (
          <div key={g.id} className="mb-16">
            <div className="text-center mb-8">
              <span
                className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full text-white mb-3"
                style={{ backgroundColor: g.color }}
              >
                {g.label}
              </span>
              <p className="text-gray-600 max-w-2xl mx-auto">{g.blurb}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {visible.filter((p) => p.group === g.id).map((product) => (
                <div
                  key={product.id}
                  onClick={() => onSelect(product.id)}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer hover:-translate-y-1 flex flex-col"
                >
                  <img src={product.image} alt={product.name} className="w-full object-cover" />
                  <div className="p-5 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-gray-800">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 flex-grow">{product.tagline}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-800 font-semibold text-sm">
                        from {product.plans[0].price}{product.plans[0].period}
                      </span>
                      <span className="text-[#004aad] font-semibold text-sm flex items-center">
                        Details <ChevronRight size={16} className="ml-1" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Request a product */}
        <div
          onClick={onContact}
          className="max-w-4xl mx-auto rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300 cursor-pointer flex flex-col md:flex-row items-center text-center md:text-left gap-6 bg-gradient-to-br from-[#004aad] to-[#003a8d] text-white"
        >
          <Zap className="w-12 h-12 flex-shrink-0" />
          <div className="flex-grow">
            <h3 className="text-2xl font-bold mb-1">Need a different integration?</h3>
            <p className="text-blue-100">Tell us the system — we build and maintain custom connectors and automations for Acumatica.</p>
          </div>
          <span className="font-semibold flex items-center flex-shrink-0">Request it <ChevronRight size={16} className="ml-1" /></span>
        </div>

        {/* How subscription works */}
        <div className="mt-20 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-10">How a subscription works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-xl shadow-md p-6">
              <Cloud className="w-8 h-8 text-[#004aad] mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">1. We set it up</h3>
              <p className="text-gray-600 text-sm">We configure the product for your Acumatica environment — no technical work on your side.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <RefreshCw className="w-8 h-8 text-[#004aad] mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">2. It runs itself</h3>
              <p className="text-gray-600 text-sm">Leads, documents, shipments and alerts flow automatically on your plan's schedule.</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <Headphones className="w-8 h-8 text-[#004aad] mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">3. We maintain it</h3>
              <p className="text-gray-600 text-sm">Monitoring, updates and support are included in every subscription. You just run your business.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Product detail page ----------
const IntegrationDetail = ({ integration: product, onBack, onContact }) => {
  const g = groupById(product.group);
  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6 max-w-6xl">
        <button onClick={onBack} className="flex items-center text-[#004aad] font-semibold mb-8 hover:text-[#003a8d] transition-colors">
          <ChevronLeft size={20} className="mr-1" /> Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-14">
          <div>
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full text-white mb-4"
              style={{ backgroundColor: g.color }}
            >
              {g.label}
            </span>
            <h1 className="text-4xl font-bold text-gray-800 mb-3">{product.name}</h1>
            <p className="text-xl text-gray-600 mb-4">{product.tagline}</p>
            <p className="text-lg font-medium text-[#004aad] mb-6">{product.positioning}</p>
            <button
              onClick={onContact}
              className="bg-[#004aad] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#003a8d] transition-colors duration-300"
            >
              Talk to Us About {product.name}
            </button>
          </div>
          <div className="flex justify-center">
            <img src={product.image} alt={product.name} className="rounded-xl shadow-2xl max-w-sm w-full" />
          </div>
        </div>

        {/* Problem / Solution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-400">
            <h2 className="text-xl font-bold text-gray-800 mb-3">The Problem</h2>
            <p className="text-gray-600">{product.problem}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-[#004aad]">
            <h2 className="text-xl font-bold text-gray-800 mb-3">The Solution</h2>
            <p className="text-gray-600">{product.solution}</p>
          </div>
        </div>

        {/* Workflow (for automation products) */}
        {product.workflow && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">How It Works</h2>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {product.workflow.map((step, i) => (
                <React.Fragment key={step}>
                  <span className="bg-blue-50 text-[#004aad] font-semibold px-4 py-2 rounded-lg text-sm">{step}</span>
                  {i < product.workflow.length - 1 && <ChevronRight size={18} className="text-gray-400" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Capabilities (IntelX AI) */}
        {product.capabilities && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Capabilities</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.capabilities.map((c) => (
                <li key={c} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#004aad] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Intelligence + Notifications (StockPulse) */}
        {product.intelligence && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Core Intelligence</h2>
              <ul className="space-y-3">
                {product.intelligence.map((c) => (
                  <li key={c} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#004aad] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Notifications</h2>
              <ul className="space-y-3">
                {product.notifications.map((c) => (
                  <li key={c} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#004aad] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Key benefits */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Key Benefits</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {product.benefits.map((b) => (
              <li key={b} className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pricing */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Subscription Plans</h2>
        <PricingPlans plans={product.plans} onContact={onContact} />

        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-3">Not sure which plan fits?</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Talk to us — we'll size the right plan for your volumes, and every subscription starts with free assisted onboarding.
          </p>
          <button
            onClick={onContact}
            className="bg-[#004aad] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#003a8d] transition-colors duration-300"
          >
            Talk to an Expert
          </button>
        </div>
      </div>
    </div>
  );
};


const App = () => {
  const [activePage, setActivePage] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);

  const openIntegration = (id) => {
    setSelectedIntegrationId(id);
    setActivePage('integrations');
    closeMenu();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


    const goToPage = (page) => {
    setActivePage(page); // switch page
    closeMenu();         // close mobile menu
    window.scrollTo({ top: 0, behavior: 'smooth' }); // scroll to top
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const products = [
    {
      id: 1,
      name: 'Acumatica',
      description: 'Cloud-based ERP built for scalability and flexibility. Manage finance, sales, inventory, and operations with real-time insights.',
      image: 'https://www.calszone.com/wp-content/uploads/acumatica-logo-stacked-2024-dark.png',
       link: 'https://www.acumatica.com/cloud-erp-software/'
    },
    {
      id: 2,
      name: 'Odoo',
      description: 'Open-source ERP with modular apps for CRM, accounting, e-commerce, and operations — endlessly customizable for growth.',
      image: 'https://download.logo.wine/logo/Odoo/Odoo-Logo.wine.png',
        link: 'https://www.odoo.com/app/website-features'
    },
    {
      id: 3,
      name: 'Sage',
      description: 'Trusted ERP and accounting solution. Simplifies financial processes, payroll, and compliance with global reliability.',
      image: 'https://logowik.com/content/uploads/images/sage7490.jpg',
        link: 'https://www.sage.com/en-ke/'
    },
    {
      id: 4,
      name: 'PaySpace',
      description: 'Modern cloud payroll and HR solution. Automates compliance and streamlines workforce management across multiple regions.',
      image: 'https://businesscloud.africa/wp-content/uploads/2022/04/Pay-Space-logo-2.jpg',
        link: 'https://www.payspace.com/'
    },
    {
      id: 5,
      name: 'GoldVision',
      description: 'CRM designed to strengthen customer relationships and integrate seamlessly with your ERP systems.',
      image: 'https://images.gv-c.com/114/Images/1000/GV_Box_Cropped_High-Res.png',
      link: 'https://www.gold-vision.com/'
    },
    {
      id: 6,
      name: 'AI-Powered ERP Enhancements',
      description: 'Future-proof your ERP with AI-driven insights, automation, and intelligent workflows. Available as an add-on to your ERP.',
      image: 'https://itchronicles.com/wp-content/uploads/2024/06/fusion-of-ERP-and-AI-1024x683.jpg',
      isAI: true
    }
  ];

  const services = [
    {
      id: 1,
      name: 'ERP, CRM and HR & Payroll Implementation',
      description: 'End-to-end implementation of systems tailored to your business needs with minimal disruption.',
      icon: <Server className="w-8 h-8 text-[#004aad]" />
    },
    
    {
      id: 2,
      name: 'System Integration',
      description: 'Seamless integration of your ERP with existing systems and third-party applications.',
      icon: <Globe className="w-8 h-8 text-[#004aad]" />
    },
    {
      id: 3,
      name: 'Custom Development',
      description: 'Custom modules and extensions to enhance your ERP functionality for unique business processes.',
      icon: <Users className="w-8 h-8 text-[#004aad]" />
    },
    {
      id: 4,
      name: 'Training & Support',
      description: 'Comprehensive training programs and ongoing support to maximize your ERP investment.',
      icon: <Headphones className="w-8 h-8 text-[#004aad]" />
    },
    {
      id: 5,
      name: 'Cloud Migration',
      description: 'Secure migration of your on-premise systems to cloud-based ERP solutions.',
      icon: <Shield className="w-8 h-8 text-[#004aad]" />
    },
    {
      id: 6,
      name: 'Consulting Services',
      description: 'Strategic consulting to optimize your business processes and ERP utilization.',
      icon: <Users className="w-8 h-8 text-[#004aad]" />
    },
    {
    id: 7,
    name: 'AI-Enhanced ERP Solutions',
    description: 'Leverage AI to automate workflows, gain predictive insights, and optimize business operations within your ERP.',
    icon: <Shield className="w-8 h-8 text-[#004aad]" />,
    isAI: true
  }
  ];

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! We will contact you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const renderHomePage = () => (
    <div className="min-h-screen">
      {/* Hero Section */}
<section 
  className="relative bg-cover bg-center text-white min-h-screen"
   style={{ 
    backgroundImage: `url(${homeBg})` 
  }}
>
  {/* Overlay */}
  <div className="absolute inset-0 bg-black/60"></div>
<div className="container mx-auto px-6 text-center relative z-10 pt-64">
  <h1 className="text-5xl font-bold mb-6">
    Empowering Businesses with Intelligent ERP, CRM, and Payroll Solutions
  </h1>
  <p className="text-xl mb-8 max-w-3xl mx-auto">
   At IntelxCode, we implement and integrate world-class ERP, CRM, and payroll solutions, 
helping organizations scale with efficiency and intelligence.

  </p>
  <button 
    onClick={() => setActivePage('products')}
    className="bg-white text-[#004aad] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-300 flex items-center mx-auto"
  >
    Explore Our ERP, CRM & HR Solutions <ChevronRight className="ml-2" size={20} />
  </button>
</div>


</section>




      {/* Trusted By Section */}
    <TrustedBySection />

      {/* Solutions Overview Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Our ERP, CRM & HR Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div 
                key={product.id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 p-6 cursor-pointer group"
                onClick={() => { setActivePage('products'); closeMenu(); }}
              >
                <div className="mb-4 overflow-hidden rounded-lg">
   <div className="h-32 w-full flex items-center justify-center bg-white rounded shadow-sm">
  <img 
    src={product.image} 
    alt={product.name}
    className="max-h-24 max-w-full object-contain"
  />
</div>

                </div>
                <h3 className={`font-bold text-lg mb-2 ${product.isAI ? 'text-[#004aad]' : 'text-gray-800'}`}>
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm">{product.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


          {/* Counter Section */}
    <CounterSection />
    <IndustrySolutions />
    <AcumaticaIntegrations onBrowse={(id) => { setSelectedIntegrationId(id); setActivePage('integrations'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}/>
    <AIBusinessBoostSection />
    <FAQSection />
    </div>
  );

const renderAIPage = () => (
  <div className="min-h-screen py-12">
    <div className="container mx-auto px-6">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">Artificial Intelligence & ERP</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          We integrate AI into ERP solutions tailored to your business needs. From predictive analytics to intelligent automation, our AI enhancements empower organizations to make smarter, data-driven decisions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition">
          <h3 className="text-2xl font-bold text-[#004aad] mb-3">Predictive Analytics</h3>
          <p className="text-gray-600">Forecast trends in sales, inventory, and customer behavior using AI-powered predictions integrated within your ERP.</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition">
          <h3 className="text-2xl font-bold text-[#004aad] mb-3">Intelligent Automation</h3>
          <p className="text-gray-600">Boost efficiency by using AI to automate recurring tasks.</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition">
          <h3 className="text-2xl font-bold text-[#004aad] mb-3">Enhanced Decision Making</h3>
          <p className="text-gray-600">Leverage AI to gain actionable insights, optimize resources, and enhance overall operational efficiency.</p>
        </div>
      </div>

      <div className="mt-12 text-center">
        <p className="text-gray-600 max-w-2xl mx-auto">
          Our AI integrations are fully customized to your ERP platform, ensuring seamless adoption and measurable impact for your business.
        </p>
      </div>
    </div>
  </div>
);


  const renderProductsPage = () => (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Our ERP, CRM & HR Solutions</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore the platforms we implement, customize, and support worldwide.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <div 
              key={product.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300"
            >
          <div className="h-48 w-full flex items-center justify-center bg-white rounded">
  <img 
    src={product.image} 
    alt={product.name}
    className="max-h-32 max-w-full object-contain"
  />
</div>

       <div className="p-6">
  <h3 className={`text-2xl font-bold mb-3 ${product.isAI ? 'text-[#004aad]' : 'text-gray-800'}`}>
    {product.name}
  </h3>
  <p className="text-gray-600 mb-4">{product.description}</p>

  {/* Only show "Learn More" if product has a link */}
  {product.link && (
    <a 
      href={product.link} 
      target="_blank" 
      rel="noopener noreferrer"
      className="text-[#004aad] font-semibold hover:text-[#003a8d] transition-colors duration-300 flex items-center"
    >
      Learn More <ChevronRight size={16} className="ml-1" />
    </a>
  )}
</div>

            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderServicesPage = () => (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Our Services</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive ERP services to transform your business operations and drive growth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div 
              key={service.id}
              className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="mb-4">
                {service.icon}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.name}</h3>
              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAboutPage = () => (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">About IntelxCode</h1>
          
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-[#004aad] mb-4">Our Story</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              IntelxCode is a global ERP, CRM, and payroll solutions provider helping businesses implement, integrate, and extend ERP platforms. 
              Our expertise spans finance, supply chain, HR, and customer engagement systems. With a team of certified professionals 
              and a deep understanding of industry-specific requirements, we deliver tailored ERP solutions that drive business transformation.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-[#004aad] mb-4">Our Vision</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Our mission is to empower organizations worldwide with intelligent, future-ready ERP systems. 
              We believe that technology should simplify complexity, not create it. By combining cutting-edge ERP platforms 
              with innovative AI enhancements, we help businesses unlock new levels of efficiency, insight, and growth potential.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactPage = () => (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600">We'd love to hear from you. Get in touch with our team.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004aad] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004aad] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004aad] focus:border-transparent"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-[#004aad] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#003a8d] transition-colors duration-300"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Get in Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <Mail className="text-[#004aad] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-800">Email</h3>
                    <p className="text-gray-600">info@intelxcode.co.ke</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="text-[#004aad] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-800">Phone</h3>
                    <p className="text-gray-600">+254 701 537 958</p>
                      <p className="text-gray-600">+254 759 621 324</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <MapPin className="text-[#004aad] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-800">Office Locations</h3>
                    <p className="text-gray-600">Nairobi, Kenya</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Globe className="text-[#004aad] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-800">Worldwide Reach</h3>
                    <p className="text-gray-600">Serving clients across 50+ countries with local expertise and global standards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsPage = () => (
    selectedIntegrationId && PRODUCTS.find((p) => p.id === selectedIntegrationId)
      ? <IntegrationDetail
          integration={PRODUCTS.find((p) => p.id === selectedIntegrationId)}
          onBack={() => setSelectedIntegrationId(null)}
          onContact={() => goToPage('contact')}
        />
      : <IntegrationsMarketplace
          onSelect={openIntegration}
          onContact={() => goToPage('contact')}
        />
  );

 const renderContent = () => {
  switch (activePage) {
    case 'home': return renderHomePage();
    case 'products': return renderProductsPage();
    case 'services': return renderServicesPage();
    case 'integrations': return renderIntegrationsPage(); // <-- integrations marketplace
    case 'ai': return renderAIPage(); // <-- new AI page
    case 'about': return renderAboutPage();
    case 'contact': return renderContactPage();
    default: return renderHomePage();
  }
};


  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
<nav className={`absolute top-0 left-0 w-full z-50 ${activePage === 'home' ? 'bg-transparent' : 'bg-gray-900'} transition-colors duration-300`}>


        <div className="container mx-auto px-6 h-28">
          <div className="flex justify-between items-center h-16">
<div className="flex items-center pt-12">
<img 
  src={logo} 
  alt="IntelxCode" 
  className="h-40 w-auto object-contain" 
/>

</div>


  {/* Desktop Navigation */}
<div className="hidden md:flex space-x-8">
  <button 
    onClick={() => { setActivePage('home'); closeMenu(); }}
    className={`font-medium transition-colors duration-300 ${activePage === 'home' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
  >
    Home
  </button>
  <button 
    onClick={() => { setActivePage('products'); closeMenu(); }}
    className={`font-medium transition-colors duration-300 ${activePage === 'products' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
  >
    Business Systems
  </button>
  <button 
    onClick={() => { setActivePage('services'); closeMenu(); }}
    className={`font-medium transition-colors duration-300 ${activePage === 'services' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
  >
    Services
  </button>

  <button
    onClick={() => { setSelectedIntegrationId(null); setActivePage('integrations'); closeMenu(); }}
    className={`font-medium transition-colors duration-300 ${activePage === 'integrations' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
  >
    Acumatica Products
  </button>

  <button 
  onClick={() => { setActivePage('ai'); closeMenu(); }}
  className={`font-medium transition-colors duration-300 ${activePage === 'ai' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
>
    AI
</button>

  <button 
    onClick={() => { setActivePage('about'); closeMenu(); }}
    className={`font-medium transition-colors duration-300 ${activePage === 'about' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
  >
    About
  </button>
  <button 
    onClick={() => { setActivePage('contact'); closeMenu(); }}
    className={`font-medium transition-colors duration-300 ${activePage === 'contact' ? 'text-white' : 'text-white hover:text-[#004aad]'}`}
  >
    Contact
  </button>
</div>


            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button 
                onClick={toggleMenu}
                className="text-gray-700 hover:text-[#004aad] transition-colors duration-300"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
          
          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4">
              <div className="flex flex-col space-y-4">
                <button 
                  onClick={() => { setActivePage('home'); closeMenu(); }}
                  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'home' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
                >
                  Home
                </button>
                <button 
                  onClick={() => { setActivePage('products'); closeMenu(); }}
                  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'products' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
                >
                  Business Systems
                </button>
                <button 
                  onClick={() => { setActivePage('services'); closeMenu(); }}
                  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'services' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
                >
                  Services
                </button>
                <button
                  onClick={() => { setSelectedIntegrationId(null); setActivePage('integrations'); closeMenu(); }}
                  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'integrations' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
                >
                  Acumatica Products
                </button>
            <button 
  onClick={() => { setActivePage('ai'); closeMenu(); }}
  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'ai' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
>
  AI
</button>





                <button 
                  onClick={() => { setActivePage('about'); closeMenu(); }}
                  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'about' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
                >              
                  About
                </button>
                <button 
                  onClick={() => { setActivePage('contact'); closeMenu(); }}
                  className={`font-medium py-2 px-4 rounded transition-colors duration-300 ${activePage === 'contact' ? 'text-[#004aad] bg-blue-50' : 'text-gray-700 hover:text-[#004aad]'}`}
                >
                  Contact
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
     <main className={activePage !== 'home' ? 'pt-32' : ''}>
  {renderContent()}
</main>


      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
             <img 
  src={logo} 
  alt="IntelxCode" 
  className="h-32 w-auto object-contain" 
/>
              <p className="text-gray-400 mb-4 max-w-md">
                Powering businesses worldwide with intelligent ERP solutions. We help organizations scale with efficiency and intelligence.
              </p>
         <div className="flex space-x-4">
  <Facebook className="text-gray-400 hover:text-white cursor-pointer" size={24} />
  <Twitter className="text-gray-400 hover:text-white cursor-pointer" size={24} />
  <a href="https://www.linkedin.com/company/intelxcode/" target="_blank" rel="noopener noreferrer">
    <Linkedin className="text-gray-400 hover:text-white cursor-pointer" size={24} />
  </a>
  <Instagram className="text-gray-400 hover:text-white cursor-pointer" size={24} />
</div>

            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
        <ul className="space-y-2">
  <li><button onClick={() => goToPage('home')} className="text-gray-400 hover:text-white transition-colors duration-300">Home</button></li>
  <li><button onClick={() => goToPage('products')} className="text-gray-400 hover:text-white transition-colors duration-300">Products</button></li>
  <li><button onClick={() => goToPage('services')} className="text-gray-400 hover:text-white transition-colors duration-300">Services</button></li>
  <li><button onClick={() => { setSelectedIntegrationId(null); goToPage('integrations'); }} className="text-gray-400 hover:text-white transition-colors duration-300">Integrations</button></li>
  <li><button onClick={() => goToPage('about')} className="text-gray-400 hover:text-white transition-colors duration-300">About</button></li>
  <li><button onClick={() => goToPage('contact')} className="text-gray-400 hover:text-white transition-colors duration-300">Contact</button></li>
</ul>

            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
              <div className="space-y-2 text-gray-400">
                <p>info@intelxcode.co.ke</p>
                <p>+254 701 537 958</p>
                 <p>+250 798 520 901</p>
                <p>Global Presence</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} IntelxCode. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
















